import {
  GetBusinessInfoResponse,
  MyBusiness,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { BusinessRole } from "@1games-ru/region-shared/dist/enums/business/role";
import { JobType } from "@1games-ru/region-shared/dist/enums/business/service";
import { useCallback, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useAppSelector from "@/core/helpers/useAppSelector";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import { ServiceCardEnum } from "@/domains/business/constants/serviceCardEnum";
import { serviceInfoSelector } from "@/domains/business/slice";
import { getServiceCardColors } from "@/domains/business/helpers/getServiceCardColors";
import useGetServiceInfo from "@/domains/business/hooks/useGetServiceInfo";
import useServiceHandlers from "@/domains/business/hooks/useServiceHandlers";
import BusinessServiceCard from "@/domains/business/components/service/BusinessServiceCard";

type Props = {
  info: MyBusiness | GetBusinessInfoResponse;
  onClose: () => void;
};

export default function BusinessServiceContainer({ info, onClose }: Props) {
  const { t } = useTranslation("business");

  const serviceInfo = useAppSelector(serviceInfoSelector);

  const { isPending, getServiceInfo } = useGetServiceInfo();
  const { startBusinessJob } = useServiceHandlers();

  const header = t("business.service.title");
  const commonEffect = serviceInfo ? serviceInfo.cleanEffect + serviceInfo.repairEffect : "";

  const footer = (
    <div className="flex justify-between items-center py-[1.3125rem] px-6 bg-dark-800/50 border border-dashed border-[#1A1A1A] rounded-2xl">
      <div className="flex flex-col gap-0.5">
        <p className="text-20 font-bold text-accent-solid">{commonEffect}%</p>
        <p className="text-15 font-medium">{t("business.service.overallImpactOnIncome")}</p>
      </div>
      <p className="w-[16.875rem] text-15 font-medium text-white/30 text-right">
        {t("business.service.impactOnIncomeDescription")}
      </p>
    </div>
  );

  const cleaningColors = useMemo(
    () => getServiceCardColors(serviceInfo?.cleanPercent),
    [serviceInfo?.cleanPercent],
  );
  const repairColors = useMemo(
    () => getServiceCardColors(serviceInfo?.repairPercent),
    [serviceInfo?.repairPercent],
  );

  const createJobHandler = useCallback(
    (jobType: JobType, isAuto: boolean) => {
      return () => info && startBusinessJob(info.id, jobType, isAuto);
    },
    [info, startBusinessJob],
  );

  const onAutoCleaning = createJobHandler(JobType.Cleaning, true);
  const onManualCleaning = createJobHandler(JobType.Cleaning, false);
  const onAutoRepair = createJobHandler(JobType.Repairing, true);
  const onManualRepair = createJobHandler(JobType.Repairing, false);

  useLayoutEffect(() => {
    if (info?.id) getServiceInfo(info.id);
  }, [getServiceInfo, info?.id]);

  return (
    <DeviceTabContentWrapper header={header} footer={footer} onClose={onClose}>
      {isPending && !serviceInfo ? (
        <div className="absolute z-10 top-1/2 left-1/2 flex items-center justify-center p-4 -translate-x-1/2 -translate-y-1/2">
          <div className="mz-loader" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3.5">
          <BusinessServiceCard
            type={ServiceCardEnum.Cleaning}
            progress={serviceInfo?.cleanPercent}
            effect={serviceInfo?.cleanEffect}
            price={serviceInfo?.cleaningPrice}
            onClickFirstButton={onAutoCleaning}
            onClickSecondButton={onManualCleaning}
            isHandleOnly={info?.role === BusinessRole.Worker}
            {...cleaningColors}
          />
          <BusinessServiceCard
            type={ServiceCardEnum.Repair}
            progress={serviceInfo?.repairPercent}
            effect={serviceInfo?.repairEffect}
            price={serviceInfo?.repairingPrice}
            onClickFirstButton={onAutoRepair}
            onClickSecondButton={onManualRepair}
            isHandleOnly={info?.role === BusinessRole.Worker}
            {...repairColors}
          />
        </div>
      )}
    </DeviceTabContentWrapper>
  );
}
